import { Fragment, createElement, type ReactNode } from "react";
import Image from "next/image";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import remarkGfm from "remark-gfm";

import assetRecords from "@/content/assets-runtime.json";
import embeddedEntryRecords from "@/content/rich-text-embeds.json";

const CONTENT_IMAGE_SIZES =
  "(max-width: 768px) 100vw, (max-width: 1280px) 720px, 800px";

const toContentImageSrc = (value: string) => {
  if (!value) {
    return "";
  }
  if (value.startsWith("//")) {
    return `https:${value}`;
  }
  return value;
};

export const BLOCKS = {
  DOCUMENT: "document",
  PARAGRAPH: "paragraph",
  HEADING_1: "heading-1",
  HEADING_2: "heading-2",
  HEADING_3: "heading-3",
  HEADING_4: "heading-4",
  HEADING_5: "heading-5",
  HEADING_6: "heading-6",
  UL_LIST: "unordered-list",
  OL_LIST: "ordered-list",
  LIST_ITEM: "list-item",
  QUOTE: "blockquote",
  HR: "hr",
  EMBEDDED_ASSET: "embedded-asset-block",
  EMBEDDED_ENTRY: "embedded-entry-block",
} as const;

export const INLINES = {
  HYPERLINK: "hyperlink",
  EMBEDDED_ENTRY: "embedded-entry-inline",
  ENTRY_HYPERLINK: "entry-hyperlink",
  ASSET_HYPERLINK: "asset-hyperlink",
} as const;

export const MARKS = {
  BOLD: "bold",
  ITALIC: "italic",
  UNDERLINE: "underline",
  CODE: "code",
} as const;

type RenderFunction = (node: any, children?: ReactNode) => ReactNode;
type MarkFunction = (children: ReactNode) => ReactNode;

interface RichTextOptions {
  renderNode?: Record<string, RenderFunction>;
  renderMark?: Record<string, MarkFunction>;
}

export type Document =
  | string
  | {
      nodeType: string;
      data?: Record<string, unknown>;
      content?: unknown[];
    };

interface AssetRecord {
  id: string;
  title: string;
  description: string;
  contentType: string;
  size: number;
  width: number | null;
  height: number | null;
  filename: string;
  url: string;
}

const assets = assetRecords as Record<string, AssetRecord>;
const embeddedEntries = embeddedEntryRecords as Record<string, any>;

function assetTarget(id: string) {
  const asset = assets[id];

  if (!asset) {
    return null;
  }

  return {
    sys: {
      id: asset.id,
      type: "Asset",
    },
    fields: {
      title: asset.title,
      description: asset.description,
      file: {
        url: asset.url.replace(/^https:/, ""),
        fileName: asset.filename,
        contentType: asset.contentType,
        details: {
          size: asset.size,
          ...(asset.width && asset.height
            ? {
                image: {
                  width: asset.width,
                  height: asset.height,
                },
              }
            : {}),
        },
      },
    },
  };
}

function textValue(children: ReactNode): string {
  if (Array.isArray(children)) {
    return children.map(textValue).join("");
  }

  if (children === null || children === undefined || children === false) {
    return "";
  }

  if (typeof children === "string" || typeof children === "number") {
    return String(children);
  }

  if (
    typeof children === "object" &&
    children !== null &&
    "props" in children &&
    (children as { props?: { children?: ReactNode } }).props
  ) {
    return textValue(
      (children as { props: { children?: ReactNode } }).props.children,
    );
  }

  return "";
}

function legacyRichTextToMarkdown(value: any): string {
  function render(node: any): string {
    if (!node || typeof node !== "object") {
      return "";
    }

    if (node.nodeType === "text") {
      return (node.marks || []).reduce((text: string, mark: any) => {
        if (mark.type === MARKS.BOLD) return `**${text}**`;
        if (mark.type === MARKS.ITALIC) return `_${text}_`;
        if (mark.type === MARKS.UNDERLINE) return `<u>${text}</u>`;
        if (mark.type === MARKS.CODE) return `\`${text}\``;
        return text;
      }, node.value || "");
    }

    const children = (node.content || []).map(render).join("");

    switch (node.nodeType) {
      case BLOCKS.DOCUMENT:
        return children;
      case BLOCKS.PARAGRAPH:
        return `${children}\n\n`;
      case BLOCKS.HEADING_1:
      case BLOCKS.HEADING_2:
      case BLOCKS.HEADING_3:
      case BLOCKS.HEADING_4:
      case BLOCKS.HEADING_5:
      case BLOCKS.HEADING_6:
        return `${"#".repeat(Number(node.nodeType.at(-1)))} ${children}\n\n`;
      case INLINES.HYPERLINK:
        return `[${children}](${node.data?.uri || "#"})`;
      case BLOCKS.LIST_ITEM:
        return children.trim();
      case BLOCKS.UL_LIST:
        return `${(node.content || [])
          .map((item: any) => `- ${render(item)}`)
          .join("\n")}\n\n`;
      case BLOCKS.OL_LIST:
        return `${(node.content || [])
          .map((item: any, index: number) => `${index + 1}. ${render(item)}`)
          .join("\n")}\n\n`;
      default:
        return children;
    }
  }

  return render(value).trim();
}

function renderWithOptions(
  nodeType: string,
  options: RichTextOptions,
  fallbackTag: string,
  children: ReactNode,
  properties: Record<string, unknown> = {},
) {
  const renderer = options.renderNode?.[nodeType];

  if (renderer) {
    return renderer(
      {
        nodeType,
        data: properties,
        content: [],
      },
      children,
    );
  }

  return createElement(fallbackTag, properties, children);
}

export function documentToReactComponents(
  value: unknown,
  options: RichTextOptions = {},
) {
  if (!value) {
    return null;
  }

  const markdown =
    typeof value === "string" ? value : legacyRichTextToMarkdown(value);

  if (!markdown.trim()) {
    return null;
  }

  const components: Record<string, any> = {
    p: ({ children }: any) =>
      renderWithOptions(
        BLOCKS.PARAGRAPH,
        options,
        "p",
        children,
      ),
    h1: ({ children }: any) =>
      renderWithOptions(BLOCKS.HEADING_1, options, "h1", children),
    h2: ({ children }: any) =>
      renderWithOptions(BLOCKS.HEADING_2, options, "h2", children),
    h3: ({ children }: any) =>
      renderWithOptions(BLOCKS.HEADING_3, options, "h3", children),
    h4: ({ children }: any) =>
      renderWithOptions(BLOCKS.HEADING_4, options, "h4", children),
    h5: ({ children }: any) =>
      renderWithOptions(BLOCKS.HEADING_5, options, "h5", children),
    h6: ({ children }: any) =>
      renderWithOptions(BLOCKS.HEADING_6, options, "h6", children),
    ul: ({ children }: any) =>
      renderWithOptions(BLOCKS.UL_LIST, options, "ul", children),
    ol: ({ children }: any) =>
      renderWithOptions(BLOCKS.OL_LIST, options, "ol", children),
    li: ({ children }: any) =>
      renderWithOptions(BLOCKS.LIST_ITEM, options, "li", children),
    blockquote: ({ children }: any) =>
      renderWithOptions(BLOCKS.QUOTE, options, "blockquote", children),
    hr: () => renderWithOptions(BLOCKS.HR, options, "hr", null),
    strong: ({ children }: any) =>
      options.renderMark?.[MARKS.BOLD]?.(children) ?? (
        <strong>{children}</strong>
      ),
    em: ({ children }: any) =>
      options.renderMark?.[MARKS.ITALIC]?.(children) ?? <em>{children}</em>,
    u: ({ children }: any) =>
      options.renderMark?.[MARKS.UNDERLINE]?.(children) ?? <u>{children}</u>,
    code: ({ children }: any) =>
      options.renderMark?.[MARKS.CODE]?.(textValue(children)) ?? (
        <code>{children}</code>
      ),
    a: ({ href = "", children }: any) => {
      if (href.startsWith("entry:")) {
        const id = href.slice("entry:".length);
        const target = embeddedEntries[id];
        const renderer = options.renderNode?.[INLINES.EMBEDDED_ENTRY];

        if (renderer && target) {
          return renderer(
            {
              nodeType: INLINES.EMBEDDED_ENTRY,
              data: { target },
              content: [],
            },
            children,
          );
        }

        return target?.fields?.title ? (
          <span>{target.fields.title}</span>
        ) : null;
      }

      if (href.startsWith("asset:")) {
        const id = href.slice("asset:".length);
        const target = assetTarget(id);
        return target ? (
          <a href={`https:${target.fields.file.url}`}>{children}</a>
        ) : (
          <Fragment>{children}</Fragment>
        );
      }

      const renderer = options.renderNode?.[INLINES.HYPERLINK];

      if (renderer) {
        return renderer(
          {
            nodeType: INLINES.HYPERLINK,
            data: { uri: href },
            content: [{ nodeType: "text", value: textValue(children) }],
          },
          children,
        );
      }

      return <a href={href}>{children}</a>;
    },
    img: ({ src = "", alt = "" }: any) => {
      if (src.startsWith("asset:")) {
        const id = src.slice("asset:".length);
        const target = assetTarget(id);
        const renderer = options.renderNode?.[BLOCKS.EMBEDDED_ASSET];

        if (renderer && target) {
          return renderer({
            nodeType: BLOCKS.EMBEDDED_ASSET,
            data: { target },
            content: [],
          });
        }

        if (!target?.fields?.file?.url) {
          return null;
        }

        const assetUrl = toContentImageSrc(target.fields.file.url);
        const width =
          target.fields.file?.details?.image?.width || 1200;
        const height =
          target.fields.file?.details?.image?.height || 800;

        return (
          <Image
            src={assetUrl}
            alt={alt || target.fields.title || ""}
            width={width}
            height={height}
            sizes={CONTENT_IMAGE_SIZES}
            className="h-auto w-full"
            style={{ width: "100%", height: "auto" }}
          />
        );
      }

      const imageSrc = toContentImageSrc(src);
      if (!imageSrc) {
        return null;
      }

      return (
        <Image
          src={imageSrc}
          alt={alt || ""}
          width={1200}
          height={800}
          sizes={CONTENT_IMAGE_SIZES}
          className="h-auto w-full"
          style={{ width: "100%", height: "auto" }}
        />
      );
    },
    "content-entry": ({ "data-id": id }: { "data-id"?: string }) => {
      if (!id) {
        return null;
      }

      const target = embeddedEntries[id];
      const renderer = options.renderNode?.[BLOCKS.EMBEDDED_ENTRY];

      if (renderer && target) {
        return renderer({
          nodeType: BLOCKS.EMBEDDED_ENTRY,
          data: { target },
          content: [],
        });
      }

      return target?.fields?.title ? (
        <span>{target.fields.title}</span>
      ) : null;
    },
  };

  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      rehypePlugins={[rehypeRaw]}
      components={components}
      transformLinkUri={(uri) => uri}
      transformImageUri={(uri) => uri}
    >
      {markdown}
    </ReactMarkdown>
  );
}
